import Phaser from 'phaser';
import { COLORS, FONTS } from '../config/theme';
import type { Player } from '../types/game';
import { getPlayerName } from '../utils/playerName';
import { getDominoBackTextureKey } from '../config/dominoAssets';
// Cosmetics: durable viewer-skin plumbing (kept when the selection source
// changes). Skin is currently selected by the temporary rule in
// config/viewerCosmetics.ts — see that file for what is and
// isn't temporary about this wiring.
import type { ViewerTileSkin } from '../config/viewerCosmetics';
import { getViewerBackTextureKey } from '../config/viewerTileSkinAssets';
import { countryFlagTextureKey } from '../config/countryFlags';
import { avatarTextureKey, identityInitial } from '../config/avatars';

/**
 * OpponentHands — draws three opponent panels around the board.
 *
 * ── Mobile layout ────────────────────────────────────────────────────────
 *
 *  ┌───────────────────────────────────────────────┐  ← top strip (partner)
 *  │ LEFT PANEL │     green board area     │ RIGHT  │
 *  │  (sidebar) │                          │ PANEL  │
 *  └───────────────────────────────────────────────┘
 *
 *  Top strip  : full-width, topPanelH px tall — partner info + tiny tile squares
 *  Left panel : sidePanelW px wide, full play-area height — left opponent
 *  Right panel: sidePanelW px wide, full play-area height — right opponent
 *
 *  Tile sizes always adapt to available space so nothing overflows.
 *
 * ── Desktop layout ───────────────────────────────────────────────────────
 *  Compact floating panels (unchanged from original).
 */
export class OpponentHands extends Phaser.GameObjects.Container {

  // Domino tile display aspect ratio: width / height (landscape orientation).
  // Matches new tile PNGs (512×256) and the game's computed unit geometry (2×unit : 1×unit).
  private static readonly BACK_RATIO = 2.0;

  // ── Layout params (set by GameScene via setLayout) ───────────────────
  private boardX          = 0;
  private boardY          = 0;
  private boardVisualSize = 0;
  private unit            = 0;
  private isMobile        = false;
  private screenW         = 0;
  private screenH         = 0;
  private playCX          = 0;
  private playCY          = 0;
  private playW           = 0;
  private playH           = 0;
  private sidePanelW      = 72;
  private topPanelH       = 56;
  private playAreaTopY    = 0;
  private playAreaBottomY = 0;

  // Cosmetics: viewer-selected tile skin (durable). Current selection
  // source is temporary — see config/viewerCosmetics.ts.
  private viewerTileSkin: ViewerTileSkin;

  constructor(scene: Phaser.Scene, viewerTileSkin: ViewerTileSkin = 'classic') {
    super(scene, 0, 0);
    this.viewerTileSkin = viewerTileSkin;
    scene.add.existing(this);
    this.setDepth(46);
  }

  /** Resolves the back texture for this viewer's skin (durable), falling
   *  back to classic if that skin's back texture failed to load. */
  private resolveBackTextureKey(): string {
    const key = getViewerBackTextureKey(this.viewerTileSkin);
    return this.scene.textures.exists(key) ? key : getDominoBackTextureKey();
  }

  // ─── Public API ───────────────────────────────────────────────────────

  setLayout(params: {
    boardX:          number;
    boardY:          number;
    boardVisualSize: number;
    unit:            number;
    isMobile:        boolean;
    screenW:         number;
    screenH?:        number;
    playCX?:         number;
    playCY?:         number;
    playW?:          number;
    playH?:          number;
    sidePanelW?:     number;
    topPanelH?:      number;
    playAreaTopY?:   number;
    playAreaBottomY?:number;
  }): void {
    this.boardX          = params.boardX;
    this.boardY          = params.boardY;
    this.boardVisualSize = params.boardVisualSize;
    this.unit            = params.unit;
    this.isMobile        = params.isMobile;
    this.screenW         = params.screenW;
    this.screenH         = params.screenH  ?? 800;
    this.playCX          = params.playCX   ?? params.boardX;
    this.playCY          = params.playCY   ?? params.boardY;
    this.playW           = params.playW    ?? 300;
    this.playH           = params.playH    ?? 400;
    this.sidePanelW      = params.sidePanelW      ?? 72;
    this.topPanelH       = params.topPanelH       ?? 56;
    this.playAreaTopY    = params.playAreaTopY     ?? (this.boardY - this.playH / 2);
    this.playAreaBottomY = params.playAreaBottomY  ?? (this.boardY + this.playH / 2);
  }

  update(
    players:     Player[],
    handCounts:  Record<string, number>,
    currentTurn: number,
    myPosition:  number,
  ): void {
    this.removeAll(true);
    if (this.unit === 0) return;

    // myPosition < 0 only ever reaches here for a spectator (GameScene skips
    // calling update() at all for a real participant whose position hasn't
    // resolved yet) — anchor at a fixed seat 0, same convention as
    // DesktopPlayerCards, so a spectator sees all four seats instead of none.
    const seatAnchor = myPosition >= 0 ? myPosition : 0;

    if (this.isMobile) {
      this.drawMobileLayout(players, handCounts, currentTurn, seatAnchor, myPosition < 0);
    } else {
      this.drawDesktopPanel('top',   (seatAnchor + 2) % 4, players, handCounts, currentTurn);
      this.drawDesktopPanel('left',  (seatAnchor + 3) % 4, players, handCounts, currentTurn);
      this.drawDesktopPanel('right', (seatAnchor + 1) % 4, players, handCounts, currentTurn);
    }
  }

  // ─── Mobile orchestration ────────────────────────────────────────────

  private drawMobileLayout(
    players:      Player[],
    handCounts:   Record<string, number>,
    currentTurn:  number,
    seatAnchor:   number,
    isSpectator:  boolean,
  ): void {
    const partnerPos = (seatAnchor + 2) % 4;
    const leftPos    = (seatAnchor + 3) % 4;
    const rightPos   = (seatAnchor + 1) % 4;

    this.drawPlayerPanel('top',    partnerPos, players, handCounts, currentTurn);
    this.drawPlayerPanel('left',   leftPos,    players, handCounts, currentTurn);
    this.drawPlayerPanel('right',  rightPos,   players, handCounts, currentTurn);
    // Bottom is normally MY seat (no tiles drawn — Hand.ts owns that spot).
    // Spectating: no local hand exists there at all, so show its tile-back
    // count too, exactly like the other three seats.
    this.drawPlayerPanel('bottom', seatAnchor, players, handCounts, currentTurn, isSpectator);
  }

  // ─── Unified player panel ────────────────────────────────────────────

  /**
   * Master panel used for all three opponent seats.
   *
   * All panels share the same design language:
   *   • Background = team color gradient, always top → bottom
   *   • Content = avatar circle + player name + face-down tiles (nothing else)
   *   • Avatar always at the "first" position (top for vertical, left for horizontal)
   *
   * Placement determines orientation and screen position:
   *   top   → horizontal strip (partner), full width, height = sidePanelW
   *   left  → vertical strip, left edge, width = sidePanelW
   *   right → vertical strip, right edge, width = sidePanelW
   */
  private drawPlayerPanel(
    placement:    'top' | 'bottom' | 'left' | 'right',
    position:     number,
    players:      Player[],
    handCounts:   Record<string, number>,
    currentTurn:  number,
    /** Bottom seat only: show its tile-back count too (see drawMobileLayout). */
    showBottomTiles = false,
  ): void {
    const player   = players.find(p => p.position === position) ?? null;
    const count    = Math.max(0, handCounts[String(position)] ?? 0);
    const isActive = currentTurn === position;
    const teamCol  = (position % 2 === 0) ? COLORS.teamA : COLORS.teamB;
    const name     = getPlayerName(player, `P${position + 1}`);

    // ── Panel bounds: floating card layout ──────────────────────────────
    const spw    = this.sidePanelW;
    const tph    = this.topPanelH;
    const playH  = this.playAreaBottomY - this.playAreaTopY;
    const sideH  = Math.min(this.screenW, playH);
    const centerY = (this.playAreaTopY + this.playAreaBottomY) / 2;

    const MARGIN      = 16;  // inset for top/bottom panels
    const SIDE_MARGIN = 8;   // inset for left/right panels — closer to edge for more board width

    let panX: number, panY: number, panW: number, panH: number;

    if (placement === 'top') {
      panW = this.screenW - MARGIN * 2;    // inset from both screen sides
      panH = Math.round(tph * 0.90);       // ~10% shorter
      panX = MARGIN;
      panY = this.playAreaTopY - panH - 4; // 4px breathing room below HUD
    } else if (placement === 'bottom') {
      panW = this.screenW - MARGIN * 2;
      panH = Math.round(tph * 0.90);
      panX = MARGIN;
      panY = this.playAreaBottomY + 4;     // 4px breathing room above hand rack
    } else if (placement === 'left') {
      panW = Math.round(spw * 0.715);
      panH = Math.min(Math.round(sideH * 0.95), 286);
      panX = SIDE_MARGIN;                                          // shifted toward edge
      panY = centerY - panH / 2;                                   // vertically centered
    } else {
      panW = Math.round(spw * 0.715);
      panH = Math.min(Math.round(sideH * 0.95), 286);
      panX = this.screenW - SIDE_MARGIN - panW;                   // shifted toward edge
      panY = centerY - panH / 2;
    }

    const cx      = panX + panW / 2;
    const CORNER_R = 10;   // fully rounded — panel floats clear of all edges

    // ── Background: floating card — shadow + gradient, no hard borders ──
    const bg = this.scene.add.graphics();

    // Soft drop shadow — skipped for bottom panel (downward shadow there reads as a black footer)
    if (placement !== 'bottom') {
      bg.fillStyle(0x000000, 0.26);
      bg.fillRoundedRect(panX + 1, panY + 3, panW, panH, CORNER_R);
    }

    // Dark base
    bg.fillStyle(0x08080f, 0.94);
    bg.fillRoundedRect(panX, panY, panW, panH, CORNER_R);

    // Team color gradient tint — top-heavy, fades to near-transparent at bottom
    bg.fillGradientStyle(teamCol, teamCol, teamCol, teamCol, 0.22, 0.22, 0.05, 0.05);
    bg.fillRoundedRect(panX, panY, panW, panH, CORNER_R);

    // No hard outlines — only multi-layer glow for the active player
    if (isActive) {
      bg.lineStyle(10, COLORS.gold, 0.09);
      bg.strokeRoundedRect(panX - 4, panY - 4, panW + 8, panH + 8, CORNER_R + 4);
      bg.lineStyle(5, COLORS.gold, 0.26);
      bg.strokeRoundedRect(panX - 1.5, panY - 1.5, panW + 3, panH + 3, CORNER_R + 1.5);
      bg.lineStyle(1.5, COLORS.gold, 0.48);
      bg.strokeRoundedRect(panX, panY, panW, panH, CORNER_R);
    } else {
      // Whisper-thin edge — defines the card without hard framing
      bg.lineStyle(1, teamCol, 0.18);
      bg.strokeRoundedRect(panX, panY, panW, panH, CORNER_R);
    }

    this.add(bg);

    // Country flag badge key — allowlist-validated (countryFlags.ts); null
    // for bots/unset/invalid, in which case no badge is drawn (the panel's
    // existing neutral presentation is the fallback).
    const flagKey = countryFlagTextureKey(player?.country_code);

    // Player Identity V1: authoritative avatar texture key (human preset or
    // stable bot assignment) — null/uncached falls back to the initial.
    const avatarTexKey = avatarTextureKey(player?.avatar_key);
    const initial      = identityInitial(name);

    // ── Content (avatar + name + tiles) ────────────────────────────────
    if (placement === 'top') {
      this.drawTopPanelContent(panX, panY, panW, panH, cx, name, teamCol, isActive, count, true, flagKey, avatarTexKey, initial);
    } else if (placement === 'bottom') {
      // Bottom = MY player panel for a real participant — avatar + name only,
      // Hand.ts draws the interactive tiles on top. Spectating: no local hand
      // occupies this spot, so show its tile-back count too (showBottomTiles).
      this.drawTopPanelContent(panX, panY, panW, panH, cx, name, teamCol, isActive, count, showBottomTiles, flagKey, avatarTexKey, initial);
    } else {
      this.drawSidePanelContent(panX, panY, panW, panH, cx, name, teamCol, isActive, count, placement, flagKey, avatarTexKey, initial);
    }
  }

  // ─── Top panel (partner) content ─────────────────────────────────────

  /**
   * Horizontal layout:  [NAME]   [tiles in a row]   [AVATAR]
   * Tiles are portrait-oriented (tall & narrow) to fill the strip height.
   */
  private drawTopPanelContent(
    panX: number, panY: number, panW: number, panH: number,
    _cx:  number,
    name: string, teamCol: number, isActive: boolean, count: number,
    showTiles: boolean,
    flagKey: string | null = null,
    avatarTexKey: string | null = null,
    initial: string = 'P',
  ): void {
    const cy  = panY + panH / 2;
    const spw = this.sidePanelW;

    // Avatar: LEFT column, slightly above center so name fits below
    const avatarR = Math.min(Math.round(panH * 0.28), 20);
    const avatarX = panX + spw / 2;
    const avatarY = panY + Math.round(panH * 0.36);
    this.drawPersonAvatar(avatarX, avatarY, avatarR, teamCol, isActive, flagKey, avatarTexKey, initial);

    // Name: directly below avatar
    const maxChars = Math.max(3, Math.floor((spw - 4) / 5));
    const dispName = name.length > maxChars ? name.slice(0, maxChars) : name;
    const nameT    = this.scene.add.text(avatarX, avatarY + avatarR + 3, dispName, {
      fontSize: '8px',
      color: isActive ? COLORS.textGold : '#cccccc',
      fontStyle: isActive ? 'bold' : 'normal',
      fontFamily: FONTS.body,
    }).setOrigin(0.5, 0);
    this.add(nameT);

    if (!showTiles || count === 0) return;

    // Tiles: portrait-oriented (taller than wide) so they fill the panel height
    const tilesLeft  = panX + spw + 6;
    const tilesRight = panX + panW - 8;
    const availW     = Math.max(10, tilesRight - tilesLeft);
    const tilePad    = 3;

    const maxTileH   = Math.round(panH * 0.74);
    const maxTileW   = Math.round(maxTileH / OpponentHands.BACK_RATIO);   // portrait: w < h

    const fittedW  = Math.floor((availW - (count - 1) * tilePad) / count);
    const tileW    = Math.max(8, Math.min(maxTileW, fittedW));
    const tileH    = Math.round(tileW * OpponentHands.BACK_RATIO);         // portrait

    const totalW   = count * tileW + (count - 1) * tilePad;
    const startX   = tilesLeft + (availW - totalW) / 2;

    for (let i = 0; i < count; i++) {
      this.addDominoBack(startX + i * (tileW + tilePad) + tileW / 2, cy, tileW, tileH);
    }
  }

  // ─── Side panel (left / right opponent) content ───────────────────────

  /**
   * Vertical layout (same for left and right):
   *   [AVATAR]  ← always at the top
   *   [NAME]    ← small horizontal text below avatar
   *   [TILE]    ← tiles stacked vertically (landscape), centered
   *   [TILE]
   *   ...
   */
  private drawSidePanelContent(
    _panX: number, panY: number, panW: number, panH: number,
    cx:   number,
    name: string, teamCol: number, isActive: boolean, count: number,
    _placement: 'left' | 'right',
    flagKey: string | null = null,
    avatarTexKey: string | null = null,
    initial: string = 'P',
  ): void {
    const edgePad  = 7;   // was 6, +10%
    const namePad  = 4;

    // Avatar: sized against a minimum so it stays readable in narrow panels
    const avatarR = Math.max(15, Math.min(Math.round(panW * 0.32), 24));   // floor 14→15, cap 22→24
    const avatarY = panY + edgePad + avatarR;
    this.drawPersonAvatar(cx, avatarY, avatarR, teamCol, isActive, flagKey, avatarTexKey, initial);

    // Name: small horizontal text, centered below avatar
    const maxChars = Math.max(3, Math.floor((panW - 4) / 5));
    const dispName = name.length > maxChars ? name.slice(0, maxChars) : name;
    const nameY    = avatarY + avatarR + namePad;
    const nameT    = this.scene.add.text(cx, nameY, dispName, {
      fontSize: '9px',   // was 8px, +10%
      color: isActive ? COLORS.textGold : '#cccccc',
      fontStyle: isActive ? 'bold' : 'normal',
      fontFamily: FONTS.body,
    }).setOrigin(0.5, 0);
    this.add(nameT);

    if (count === 0) return;

    // Tiles: landscape (wider than tall), stacked vertically
    const nameTextH  = 11;   // approximate pixel height of 9px text
    const topReserve = edgePad + avatarR * 2 + namePad + nameTextH + 8;
    const tilesTopY    = panY + topReserve;
    const tilesBottomY = panY + panH - edgePad;
    const availH       = Math.max(10, tilesBottomY - tilesTopY);

    const maxTileW = Math.max(28, Math.round(panW * 0.78));
    const maxTileH = Math.round(maxTileW / OpponentHands.BACK_RATIO);
    const tilePad  = 4;

    const neededH = count * (maxTileH + tilePad) - tilePad;
    let tileW = maxTileW;
    let tileH = maxTileH;
    if (neededH > availH && count > 0) {
      tileH = Math.max(5, Math.floor((availH - (count - 1) * tilePad) / count));
      tileW = Math.min(maxTileW, Math.round(tileH * OpponentHands.BACK_RATIO));
    }

    const totalTilesH = count * tileH + (count - 1) * tilePad;
    const startY      = tilesTopY + (availH - totalTilesH) / 2;

    for (let i = 0; i < count; i++) {
      this.addDominoBack(cx, startY + i * (tileH + tilePad) + tileH / 2, tileW, tileH);
    }
  }

  // ─── Avatar helper ────────────────────────────────────────────────────

  /**
   * Draw the player avatar inside the existing circle.
   *
   * Player Identity V1: the authoritative avatar texture (human preset or
   * per-session stable bot assignment, loaded on demand — see
   * avatarTextures.ts) fills the circle when cached; otherwise the canonical
   * display-name initial renders — never a blank circle, never a random
   * client pick. Active-player glow, ring colors, circle geometry, and the
   * flag badge (bottom-right, humans only) are byte-for-byte unchanged.
   */
  private drawPersonAvatar(
    cx: number, cy: number, r: number,
    teamCol: number, isActive: boolean,
    flagKey: string | null = null,
    avatarTexKey: string | null = null,
    initial: string = 'P',
  ): void {
    const gfx = this.scene.add.graphics();

    // Dark background circle
    gfx.fillStyle(0x12121e, 0.95);
    gfx.fillCircle(cx, cy, r);

    // Multi-ring glow for the active player (simulates pulse emphasis)
    if (isActive) {
      gfx.lineStyle(10, COLORS.gold, 0.07);
      gfx.strokeCircle(cx, cy, r + 7);
      gfx.lineStyle(5, COLORS.gold, 0.18);
      gfx.strokeCircle(cx, cy, r + 3.5);
      gfx.lineStyle(2, COLORS.gold, 0.50);
      gfx.strokeCircle(cx, cy, r + 1);
    }

    // Colored ring border
    gfx.lineStyle(isActive ? 2.5 : 2.0, isActive ? COLORS.gold : teamCol, isActive ? 1.0 : 0.80);
    gfx.strokeCircle(cx, cy, r);

    this.add(gfx);

    if (avatarTexKey && this.scene.textures.exists(avatarTexKey)) {
      // Preset avatar art is circular with transparent corners — it drops
      // into the ring at the circle's diameter with no extra masking.
      const face = this.scene.add.image(cx, cy, avatarTexKey);
      face.setDisplaySize(r * 2 - 3, r * 2 - 3); // sit just inside the ring
      this.add(face);
    } else {
      // Initial fallback — canonical display-name initial (white; gold when
      // active, matching the old silhouette's emphasis rule).
      const initialText = this.scene.add.text(cx, cy, initial, {
        fontSize:   `${Math.max(9, Math.round(r * 0.95))}px`,
        color:      isActive ? COLORS.textGold : '#ffffff',
        fontFamily: FONTS.body,
        fontStyle:  'bold',
      }).setOrigin(0.5, 0.5);
      this.add(initialText);
    }

    // Country flag badge — bottom-right corner of the avatar circle, drawn
    // only when the validated texture is cached (countryFlags.ts). No badge
    // for bots/unset/invalid — the avatar/initial alone is the neutral
    // fallback. Placement unchanged.
    if (flagKey && this.scene.textures.exists(flagKey)) {
      const fw = Math.max(12, Math.round(r * 0.9));
      const fh = Math.round(fw * 0.75);
      const flag = this.scene.add.image(cx + r * 0.72, cy + r * 0.72, flagKey);
      flag.setDisplaySize(fw, fh);
      this.add(flag);
    }
  }

  // ─── Domino-back tile drawing ────────────────────────────────────────

  /** Render a single face-down domino tile using the canonical back texture. */
  private addDominoBack(cx: number, cy: number, w: number, h: number): void {
    const img = this.scene.add.image(cx, cy, this.resolveBackTextureKey());
    img.setDisplaySize(w, h);
    this.add(img);
  }

  // ─── Desktop panels (unchanged) ──────────────────────────────────────

  private drawDesktopPanel(
    placement:   'top' | 'left' | 'right',
    position:    number,
    players:     Player[],
    handCounts:  Record<string, number>,
    currentTurn: number,
  ): void {
    const player      = players.find(p => p.position === position) ?? null;
    const count       = Math.max(0, handCounts[String(position)] ?? 0);
    const isActive    = currentTurn === position;
    const teamColor   = (position % 2 === 0) ? COLORS.teamA : COLORS.teamB;
    const accentColor = isActive ? COLORS.gold : teamColor;
    // Canonical display-name initial — never derived from email (Identity V1).
    const initial     = identityInitial(getPlayerName(player, String(position + 1)));

    let tileW: number, tileH: number, avatarR: number;

    if (placement === 'left' || placement === 'right') {
      const sideGap = Math.max(20, this.boardX - this.boardVisualSize / 2);
      tileW   = Math.round(sideGap * 0.72);
      tileH   = Math.round(tileW / OpponentHands.BACK_RATIO);
      avatarR = Math.max(14, Math.round(tileW * 0.38));
    } else {
      tileH   = Math.max(14, Math.round(this.unit * 0.38));
      tileW   = Math.round(tileH * OpponentHands.BACK_RATIO);
      avatarR = Math.max(13, Math.round(this.unit * 0.40));
    }

    const tilePad  = Math.max(2, Math.round(tileH * 0.12));
    const panelPad = Math.max(4, Math.round(avatarR * 0.35));

    if (placement === 'top') {
      const tilesW  = count > 0 ? count * tileW + (count - 1) * tilePad : 0;
      const panelW  = panelPad + avatarR * 2 + (tilesW > 0 ? panelPad + tilesW : 0) + panelPad;
      const panelH  = Math.max(avatarR * 2, tileH) + panelPad * 2;
      const { cx, cy } = this.desktopTopCenter(panelH);
      const gfx = this.desktopMakeBg(cx, cy, panelW, panelH, accentColor, isActive);
      this.add(gfx);
      const avX = cx - panelW / 2 + panelPad + avatarR;
      this.desktopDrawAvatar(gfx, avX, cy, avatarR, teamColor, accentColor, isActive);
      this.desktopAddInitial(initial, avX, cy, avatarR);
      if (count > 0) {
        const startX = cx - panelW / 2 + panelPad + avatarR * 2 + panelPad;
        for (let i = 0; i < count; i++) {
          this.addDominoBack(startX + i * (tileW + tilePad) + tileW / 2, cy, tileW, tileH);
        }
      }
    } else {
      const tilesH  = count > 0 ? count * tileH + (count - 1) * tilePad : 0;
      const panelW  = Math.max(avatarR * 2, tileW) + panelPad * 2;
      const panelH  = panelPad + avatarR * 2 + (tilesH > 0 ? panelPad + tilesH : 0) + panelPad;
      const { cx, cy } = this.desktopSideCenter(placement, panelW, panelH);
      const gfx = this.desktopMakeBg(cx, cy, panelW, panelH, accentColor, isActive);
      this.add(gfx);
      const avY = cy - panelH / 2 + panelPad + avatarR;
      this.desktopDrawAvatar(gfx, cx, avY, avatarR, teamColor, accentColor, isActive);
      this.desktopAddInitial(initial, cx, avY, avatarR);
      if (count > 0) {
        const startY = avY + avatarR + panelPad;
        for (let i = 0; i < count; i++) {
          this.addDominoBack(cx, startY + i * (tileH + tilePad) + tileH / 2, tileW, tileH);
        }
      }
    }
  }

  // ─── Desktop helpers ─────────────────────────────────────────────────

  private desktopMakeBg(cx: number, cy: number, w: number, h: number, accent: number, active: boolean) {
    const gfx = this.scene.add.graphics();
    gfx.fillStyle(0x000000, 0.55);
    gfx.fillRoundedRect(cx - w / 2, cy - h / 2, w, h, 5);
    gfx.lineStyle(active ? 2 : 1, accent, active ? 1.0 : 0.40);
    gfx.strokeRoundedRect(cx - w / 2, cy - h / 2, w, h, 5);
    return gfx;
  }

  private desktopDrawAvatar(gfx: Phaser.GameObjects.Graphics, cx: number, cy: number, r: number, teamCol: number, accent: number, active: boolean) {
    gfx.fillStyle(active ? COLORS.gold : teamCol, active ? 0.28 : 0.32);
    gfx.fillCircle(cx, cy, r);
    gfx.lineStyle(active ? 2 : 1, accent, active ? 1.0 : 0.7);
    gfx.strokeCircle(cx, cy, r);
  }

  private desktopAddInitial(text: string, cx: number, cy: number, r: number) {
    const fs = Math.max(8, Math.round(r * 1.1));
    const t  = this.scene.add.text(cx, cy, text, {
      fontSize: `${fs}px`, color: COLORS.textWhite, fontStyle: 'bold', fontFamily: FONTS.body,
    }).setOrigin(0.5);
    this.add(t);
  }

  private desktopTopCenter(panelH: number): { cx: number; cy: number } {
    return {
      cx: this.boardX,
      cy: this.boardY - this.boardVisualSize / 2 + panelH / 2 + 6,
    };
  }

  private desktopSideCenter(placement: 'left' | 'right', _panelW: number, _panelH: number): { cx: number; cy: number } {
    const boardLeftEdge  = this.boardX - this.boardVisualSize / 2;
    const boardRightEdge = this.boardX + this.boardVisualSize / 2;
    const cx = placement === 'left'
      ? boardLeftEdge / 2
      : boardRightEdge + (this.screenW - boardRightEdge) / 2;
    return { cx, cy: this.boardY };
  }
}
